import { useEffect, useState } from "react";
import { DeviceMobile, X, DownloadSimple, Share } from "@phosphor-icons/react";

/**
 * Floating "Install DispatchHub" banner shown on:
 *   • Chromium / Android Chrome — triggers native beforeinstallprompt flow
 *   • Safari on iOS             — falls back to Share → Add to Home Screen hint
 *
 * Silent when the app is already installed (display-mode: standalone) or dismissed.
 */
const DISMISS_KEY = "dh_pwa_install_dismissed_v1";

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible]   = useState(false);
  const [iosHint, setIosHint]   = useState(false);

  useEffect(() => {
    // Skip if already installed or previously dismissed
    if (window.matchMedia("(display-mode: standalone)").matches) return;
    if (window.navigator.standalone) return;
    if (localStorage.getItem(DISMISS_KEY)) return;

    const onBip = (e) => { e.preventDefault(); setDeferred(e); setVisible(true); };
    window.addEventListener("beforeinstallprompt", onBip);

    // iOS Safari: no beforeinstallprompt → show manual hint after 4s
    const ua = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua) && !window.MSStream;
    const isSafari = isIOS && /safari/.test(ua) && !/crios|fxios/.test(ua);
    if (isSafari) {
      const t = setTimeout(() => { setIosHint(true); setVisible(true); }, 4000);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", onBip); };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") localStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
    setDeferred(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed inset-x-3 bottom-3 sm:right-4 sm:left-auto sm:bottom-4 sm:w-96 z-40
                 bg-slate-900 text-white border border-slate-700 rounded-sm shadow-2xl
                 flex items-start gap-3 p-4"
      style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}
      role="dialog"
      aria-label="Install DispatchHub"
    >
      <div className="h-11 w-11 rounded-sm bg-orange-600 grid place-items-center shrink-0">
        <DeviceMobile size={22} weight="bold" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold">Install DispatchHub</div>
        {iosHint ? (
          <div className="text-xs text-slate-300 mt-1 leading-relaxed">
            Tap <Share size={12} className="inline align-[-2px] mx-0.5"/> Share, then
            <span className="font-semibold text-white"> "Add to Home Screen"</span> to launch full-screen.
          </div>
        ) : (
          <div className="text-xs text-slate-300 mt-1 leading-relaxed">
            Launches full-screen, works offline for read views, and keeps your session between shifts.
          </div>
        )}
        {!iosHint && (
          <div className="mt-3 flex gap-2">
            <button
              data-testid="pwa-install-btn"
              onClick={install}
              className="text-xs font-semibold bg-orange-600 hover:bg-orange-700 text-white px-3 py-1.5 rounded-sm inline-flex items-center gap-1">
              <DownloadSimple size={12} weight="bold"/> Install
            </button>
            <button
              data-testid="pwa-install-later"
              onClick={dismiss}
              className="text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-slate-100 px-3 py-1.5 rounded-sm">
              Not now
            </button>
          </div>
        )}
      </div>

      <button data-testid="pwa-install-close" onClick={dismiss} className="text-slate-400 hover:text-white p-1 -mt-1 -mr-1">
        <X size={16}/>
      </button>
    </div>
  );
}
